import { useEffect } from 'react';
import type { Editor } from '@tiptap/react';

import { BAND_SELECTOR, getHeaderFooterRoot } from 'containers/Tiptap/lib/header-footer-dom';

const asElement = (node: Node): Element | null => (node instanceof Element ? node : node.parentElement);

const touchesBand = (record: MutationRecord): boolean => {
  if (asElement(record.target)?.closest(BAND_SELECTOR)) return true;
  return Array.from(record.addedNodes).some(
    (node) =>
      node instanceof Element && (node.matches(BAND_SELECTOR) || node.querySelector(BAND_SELECTOR) !== null),
  );
};

// The rendered band and the portaled overlay are plain DOM, not React node views.
export function useHeaderFooterDomPatch(editor: Editor | null, apply: () => void): void {
  useEffect(() => {
    if (!editor) return undefined;

    apply();

    // No stable band node to observe: they're rebuilt on every repagination, overlay portaled to <body>.
    const observer = new MutationObserver((records) => {
      if (records.some(touchesBand)) apply();
    });
    observer.observe(getHeaderFooterRoot(editor), {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['src'],
    });
    editor.on('transaction', apply);

    return () => {
      observer.disconnect();
      editor.off('transaction', apply);
    };
  }, [editor, apply]);
}
